import { AutocompleteWithLabel, SelectWithLabel } from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import { MenuItem } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { PROVIDERS_SUBMIT_LIST_QUERY } from '../../graphql/accommodationProviderSubmit'
import { getTranslate, lang } from '../../localization'
import { ProviderAdapter } from '../../types/accommodation'
import { ProviderSubmit } from '../../types/accommodationProviderSubmit'
import useStyles from './styles/pmsMappingFilters.styles'

type PMSMappingFiltersProps = {
  autoCompleteForProvider: any
  setAutoCompleteForProvider: React.Dispatch<any>
  adapter: string
  setAdapter: React.Dispatch<React.SetStateAction<ProviderAdapter>>
}

export const PMSMappingFilters: React.FC<PMSMappingFiltersProps> = ({
  autoCompleteForProvider,
  setAutoCompleteForProvider,
  adapter,
  setAdapter
}: PMSMappingFiltersProps) => {
  const classes = useStyles()
  const params = useParams<{ propertyCode: string }>()

  const [PMSList, setPMSList] = useState<ProviderAdapter[]>([])

  const [
    getProviderConnectionList,
    { data: providerListData, loading: loadingForProviderSubmitList }
  ] = useLazyQuery<{
    providersSubmitList: ProviderSubmit[]
  }>(PROVIDERS_SUBMIT_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      toast.error(error.message)
    }
  })

  useEffect(() => {
    getProviderConnectionList({
      variables: {
        propertyCode: params.propertyCode
      }
    })
  }, [])

  useEffect(() => {
    if (autoCompleteForProvider && providerListData?.providersSubmitList) {
      const A = providerListData.providersSubmitList.find(
        (item) => item.providerCode === autoCompleteForProvider.value
      )

      if (A) {
        const adapters: ProviderAdapter[] = [
          A.providerConnections.availability.providerAdapter as ProviderAdapter,
          A.providerConnections.booking.providerAdapter as ProviderAdapter,
          A.providerConnections.rate.providerAdapter as ProviderAdapter,
          A.providerConnections.restriction.providerAdapter as ProviderAdapter
        ].filter(Boolean) // removes undefined, null, or empty string

        const uniqueAdapters = Array.from(new Set(adapters))

        setPMSList(uniqueAdapters)
      }
    }
  }, [autoCompleteForProvider, providerListData])

  return (
    <div className={classes.root}>
      <AutocompleteWithLabel
        value={autoCompleteForProvider}
        size="small"
        inputSize="small"
        label={getTranslate('Provider')}
        placeholder={getTranslate('Select provider')}
        onChange={(_, v: any) => {
          if (v) {
            setAutoCompleteForProvider({
              value: v.value,
              label: v.label
            })
          } else {
            setAutoCompleteForProvider(null)
          }
        }}
        getOptionLabel={(option) => option.label || ''}
        getOptionSelected={(option, value) => option.value === value.value}
        options={
          providerListData?.providersSubmitList
            ? providerListData.providersSubmitList.map((p) => ({
                label:
                  lang === 'en'
                    ? p.providerName || ''
                    : p.providerNameTranslatedList
                    ? p.providerNameTranslatedList[0].value
                    : '',
                value: p.providerCode || ''
              }))
            : []
        }
        loading={loadingForProviderSubmitList}
        loadingText={getTranslate('Loading') + '...'}
      />
      <SelectWithLabel
        disabled={!autoCompleteForProvider}
        fullWidth
        value={adapter}
        onChange={(event) => setAdapter(event.target.value as ProviderAdapter)}
        size="small"
        label={getTranslate('PMS Type')}>
        {PMSList.map((pms) => (
          <MenuItem
            classes={{ root: lang === 'fa' ? classes.menuItem : undefined }}
            key={pms}
            value={pms}>
            {getTranslate(pms)}
          </MenuItem>
        ))}
      </SelectWithLabel>
    </div>
  )
}
